import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Sparkles } from "lucide-react";
import BrandMark from "../components/BrandMark";
import { motion } from "framer-motion";

const highlights = [
  { title: "Signature Facials", desc: "Radiance-boosting rituals", img: "https://images.pexels.com/photos/9335961/pexels-photo-9335961.jpeg" },
  { title: "Precision Threading", desc: "Perfect brows, always", img: "https://images.unsplash.com/photo-1519415387722-a1c3bbef716c" },
  { title: "Gold Body Polish", desc: "24k luxury smoothness", img: "https://images.unsplash.com/photo-1707851621528-c50882570d7c" },
];

export const Home = () => {
  return (
    <div className="relative" data-testid="home-page">
      {/* Hero */}
      <div className="relative">
        <div className="relative h-[520px] overflow-hidden rounded-b-[2.5rem]">
          <img
            src="https://images.pexels.com/photos/37229288/pexels-photo-37229288.jpeg"
            alt="Serene facial"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/70 to-transparent" />

          <div className="relative z-10 pt-10 px-6 flex items-center justify-between">
            <BrandMark size={44} />
            <div className="text-right">
              <p className="script text-2xl text-gold leading-none">bonjour ♡</p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="absolute bottom-8 left-6 right-6"
          >
            <p className="script text-3xl text-gold mb-2" data-testid="hero-script">welcome to</p>
            <h1 className="text-5xl leading-[0.95] text-espresso font-serif">
              Maple Charm<br />
              <span className="italic text-blushDeep">Beauty Studio</span>
            </h1>
            <p className="text-mocha mt-4 max-w-[280px] text-sm leading-relaxed">
              A warm little sanctuary for radiant skin, softer brows, and quiet luxury.
            </p>
          </motion.div>
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 mt-6">
        <Link
          to="/book"
          data-testid="hero-book-btn"
          className="group flex items-center justify-between bg-espresso text-cream rounded-full pl-6 pr-2 py-2 shadow-soft hover:shadow-glow transition-shadow"
        >
          <span className="text-sm tracking-wide">Book your appointment</span>
          <span className="w-10 h-10 rounded-full bg-blush text-espresso flex items-center justify-center group-hover:translate-x-1 transition-transform">
            <ArrowRight size={18} />
          </span>
        </Link>
      </div>

      {/* Section: What we do */}
      <div className="px-6 mt-12">
        <p className="script text-2xl text-gold mb-1">a little of everything</p>
        <h2 className="text-3xl font-serif text-espresso mb-6 leading-tight">
          Rituals crafted with <em className="text-blushDeep">care</em>
        </h2>

        <div className="space-y-4">
          {highlights.map((h, idx) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08 }}
              className="flex items-center gap-4 bg-white rounded-[2rem] p-3 shadow-soft"
              data-testid={`highlight-${idx}`}
            >
              <img
                src={h.img}
                alt={h.title}
                className="w-20 h-20 rounded-2xl object-cover"
              />
              <div className="flex-1">
                <h3 className="text-xl font-serif text-espresso">{h.title}</h3>
                <p className="text-mocha text-sm">{h.desc}</p>
              </div>
              <Link
                to="/services"
                className="w-9 h-9 rounded-full bg-creamDeep flex items-center justify-center hover:bg-blush/50 transition"
                aria-label="View services"
              >
                <ArrowRight size={16} className="text-espresso" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Section: Guarantee strip */}
      <div className="px-6 mt-12">
        <div className="rounded-[2rem] bg-gradient-to-br from-blush/30 to-creamDeep p-6 relative overflow-hidden">
          <Sparkles className="absolute top-4 right-4 text-gold" size={22} />
          <p className="script text-2xl text-gold">the maple promise</p>
          <h3 className="text-2xl font-serif text-espresso mt-1 mb-3 leading-tight">
            Clean products.<br />Warm hands.<br />Real care.
          </h3>
          <p className="text-mocha text-sm leading-relaxed">
            Every treatment is tailored to your skin, mood, and moment. Because you deserve a pause that feels like yours.
          </p>
        </div>
      </div>

      {/* Reviews teaser */}
      <div className="px-6 mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-3xl font-serif text-espresso">Loved by locals</h2>
          <Link to="/contact" className="text-sm text-gold underline underline-offset-4" data-testid="see-all-reviews">See all</Link>
        </div>
        <div className="bg-white rounded-[2rem] p-6 shadow-soft">
          <div className="flex items-center gap-1 mb-3">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} className="fill-gold text-gold" />
            ))}
          </div>
          <p className="text-espresso text-lg font-serif italic leading-snug">
            "Absolutely dreamy. My skin has never looked so radiant."
          </p>
          <p className="text-mocha text-sm mt-3">— Emily R. · Signature Glow Facial</p>
        </div>
      </div>

      <div className="text-center mt-16 mb-4">
        <p className="script text-2xl text-gold">see you soon ♡</p>
      </div>
    </div>
  );
};

export default Home;
